import React, { useState } from 'react';
import { TextField, Box, Typography, Button, CircularProgress } from '@material-ui/core';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const GENERATE_PARTIAL_SALE_AI_DESCRIPTION = gql`
  mutation generatePartialSaleAiDescription(
    $numberOfClients: Int!,
    $assetsUnderManagement: Float!,
    $feeBasedRevenue: Float!,
    $trailerRevenue: Float!,
    $directRevenue: Float!,
    $commissionRevenue: Float!,
    $clientDemographicsDetails: String,
    $servicesProvidedDetails: String,
    $states: [String!]
  ) {
    generatePartialSaleAiDescription(
      numberOfClients: $numberOfClients,
      assetsUnderManagement: $assetsUnderManagement,
      feeBasedRevenue: $feeBasedRevenue,
      trailerRevenue: $trailerRevenue,
      directRevenue: $directRevenue,
      commissionRevenue: $commissionRevenue,
      clientDemographicsDetails: $clientDemographicsDetails,
      servicesProvidedDetails: $servicesProvidedDetails,
      states: $states
    ) {
      name
      description
    }
  }
`;

const Description = ({ formData, handleChange, user, company }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateAiDescription] = useMutation(GENERATE_PARTIAL_SALE_AI_DESCRIPTION, {
    onCompleted: (data) => {
      const { name, description } = data.generatePartialSaleAiDescription;
      
      // Update both name and description fields by creating synthetic events
      const nameEvent = {
        target: { name: 'segmentDetails', value: name }
      };
      const descEvent = {
        target: { name: 'description', value: description }
      };
      
      // Call handleChange for both fields
      handleChange(nameEvent);
      handleChange(descEvent);
      
      setIsGenerating(false);
    },
    onError: (error) => {
      console.error('Error generating AI content:', error);
      alert('There was an error generating the content. Please try again.');
      setIsGenerating(false);
    }
  });

  const handleGenerateWithAI = async () => {
    if (formData.numberOfClients <= 0) {
      alert('Please complete the Financial Details step first before generating name and description.');
      return;
    }

    setIsGenerating(true);
    
    generateAiDescription({
      variables: {
        numberOfClients: parseInt(formData.numberOfClients) || 0,
        assetsUnderManagement: parseFloat(formData.assetsUnderManagement) || 0.0,
        feeBasedRevenue: parseFloat(formData.feeBasedRevenue) || 0.0,
        trailerRevenue: parseFloat(formData.trailerRevenue) || 0.0,
        directRevenue: parseFloat(formData.directRevenue) || 0.0,
        commissionRevenue: parseFloat(formData.commissionRevenue) || 0.0,
        clientDemographicsDetails: formData.clientDemographicsDetails || null,
        servicesProvidedDetails: formData.servicesProvidedDetails || null,
        states: formData.states || []
      }
    });
  };

  return (
    <Box>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Typography variant="h6" gutterBottom>
        Title & Description
      </Typography>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2">
          Create a compelling name and description for your listing
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateWithAI}
          disabled={isGenerating || formData.numberOfClients <= 0}
          startIcon={
            isGenerating ? null : <FontAwesomeIcon icon={['fas', 'robot']} />
          }
          style={{ 
            minWidth: '200px',
            backgroundColor: 'var(--succession-primary, #5489B6)',
            color: 'white',
            borderColor: 'var(--succession-primary, #5489B6)'
          }}
        >
          {isGenerating ? (
            <>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                style={{ 
                  marginRight: '8px', 
                  animation: 'spin 1s linear infinite',
                  color: 'white'
                }}
              >
                <circle 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4" 
                  strokeOpacity="0.25"
                />
                <path 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
                  fill="currentColor"
                />
              </svg>
              <span style={{ color: 'white' }}>Generating...</span>
            </>
          ) : (
            <span style={{ color: 'white' }}>Generate Name & Description With AI</span>
          )}
        </Button>
      </Box>

      <Typography variant="body2" gutterBottom>
        Please give this segment a name
      </Typography>
      <TextField
        label="Name"
        name="segmentDetails"
        value={formData.segmentDetails}
        onChange={handleChange}
        fullWidth
        multiline
        margin="dense"
        inputProps={{ maxLength: 100 }}
      />
      <br/>
      <br/>
      <Typography variant="body2">
        Describe the segement you are looking to sell in 250 characters or less.
      </Typography>
      <TextField
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleChange}
        fullWidth
        multiline
        minRows={4}
        margin="dense"
        inputProps={{ maxLength: 250 }}
      />
      <Box mt={2} p={2} style={{ 
        backgroundColor: '#f5f5f5', 
        borderLeft: '4px solid #ff9800',
        borderRadius: '4px'
      }}>
        <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
          AI Privacy & Security
        </Typography>
        <Typography variant="body2" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
          Our AI features are designed with privacy, security, and fairness in mind. Client data is never used to train external AI models, and all use of AI complies with applicable privacy, confidentiality, and information security requirements. Please always review the details for accuracy and suitability as outputs may contain mistakes.
        </Typography>
      </Box>
    </Box>
  );
};

export default Description;
